import {
  Button,
  Link as FluentLink,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Body1Strong,
} from '@fluentui/react-components';
import { AddRegular } from '@fluentui/react-icons';

import { Bubble, DateLabel, TabContent } from '@shared/components';
import { AvatarGroup } from '@modules/users';
import { TDocument, isTDocument } from '@modules/documents';
import { TFile } from '@modules/files';
import { TPermissions, useCanEdit } from '@modules/access';
import { useMemo, useState } from 'react';
import { TViewApprovalDialogState } from '../../types';
import ApprovalStatusButton from '../ApprovalStatusButton';
import getInitialApprovalUsers from '../../helpers/getInitialApprovalUsers.helper';
import EditInitialApprovalUsersDialog from '../EditInitialApprovalUsers';

type TProps = {
  item: TDocument | TFile;
  permissions: TPermissions;
  setViewApprovalDialogState: (state?: TViewApprovalDialogState) => void;
};

const ApprovalsTab = ({ item, permissions, setViewApprovalDialogState }: TProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { canEdit } = useCanEdit(permissions.accessRight);
  const itemType = isTDocument(item) ? 'DOCUMENT' : 'FILE';

  const approvals = useMemo(() => {
    return item.approvals
      .slice()
      .reverse()
      .sort((a, b) => (!a.dateCompleted === !b.dateCompleted ? 0 : !a.dateCompleted ? -1 : 1)); // !dateCompleted is true if the approval request is open
  }, [item.approvals]);
  const initialApprovalUsers = useMemo(() => getInitialApprovalUsers(item), [item]);

  return (
    <TabContent>
      <Bubble>
        <EditInitialApprovalUsersDialog
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          item={item}
          itemType={itemType}
          canEdit={canEdit}
          initialApprovalUsers={initialApprovalUsers}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell key="opened" style={{ width: '120px' }}>
                <Body1Strong>Date of request</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="version" style={{ width: '100px' }}>
                <Body1Strong>Version</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="status" style={{ width: '100px' }}>
                <Body1Strong>Status</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="approved" style={{ minWidth: '100px' }}>
                <Body1Strong>Closed</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="authors" style={{ width: '100px' }}>
                <Body1Strong>Authors</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="reviewers" style={{ width: '100px' }}>
                <Body1Strong>Reviewers</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell key="approvers" style={{ width: '100px' }}>
                <Body1Strong>Approvers</Body1Strong>
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {approvals.map((approval) => (
              <TableRow key={approval._id}>
                <TableCell>
                  <FluentLink onClick={() => setViewApprovalDialogState({ isOpen: true, approvalId: approval._id })}>
                    <DateLabel short timeZone="UTC">
                      {approval.dateOpened}
                    </DateLabel>
                  </FluentLink>
                </TableCell>
                <TableCell>v{approval.versionId}</TableCell>
                <TableCell>
                  <ApprovalStatusButton
                    approval={approval}
                    setViewApprovalDialogState={setViewApprovalDialogState}
                    textOnly
                  />
                </TableCell>
                <TableCell>
                  {approval.dateCompleted ? (
                    <DateLabel short timeZone="UTC">
                      {approval.dateCompleted}
                    </DateLabel>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {approval.phases.AUTHORING && (
                    <AvatarGroup ids={approval.phases.AUTHORING.users.map((u) => u.user)} />
                  )}
                </TableCell>
                <TableCell>
                  {approval.phases.REVIEWING && (
                    <AvatarGroup ids={approval.phases.REVIEWING.users.map((u) => u.user)} />
                  )}
                </TableCell>
                <TableCell>
                  {approval.phases.APPROVING && (
                    <AvatarGroup ids={approval.phases.APPROVING.users.map((u) => u.user)} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {item.approvals.length < 1 && <p className="pl-2">No approval request exist yet.</p>}
        <div className="mt-4">
          {initialApprovalUsers ? (
            <Button appearance="primary" onClick={() => setIsDialogOpen(true)}>
              {canEdit ? 'Edit original approval' : 'View original approval'}
            </Button>
          ) : (
            canEdit && (
              <Button appearance="secondary" icon={<AddRegular />} onClick={() => setIsDialogOpen(true)}>
                Add original approval
              </Button>
            )
          )}
        </div>
      </Bubble>
    </TabContent>
  );
};

export default ApprovalsTab;
